import { getInstance } from './instanceBBA';

// Support tooling: list every draft version of a member's book and switch which one the
// editor opens. Talks directly to the BBA cover-editor service (same pattern as
// offer.ts / `abo/book/packages`): shared api key in `?key=`, operator email in the
// `X-Admin-Identity` header for upstream audit logs. The book is identified from a
// support-friendly (email, draftId) pair — the backend validates the draft belongs to
// the given customer email.

const instance = getInstance();

export interface DraftVersion {
  draftId: string;
  name: string;
  milestone: string;
  status: string;
  percentComplete?: number;
  parentDraftId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  thumbnailUrl?: string | null;
  previewUrl?: string | null;
  linksStatus?: string | null;
  bookUrl?: string | null;
  isCurrent: boolean;
}

export interface BookDraftVersionsResponse {
  userId: string;
  bookId: string;
  currentDraftId: string | null;
  versions: DraftVersion[];
}

// set-current on a done/inreview draft does not unlock it: BBA makes an editable copy
// current instead (reusing an untouched copy if one exists).
export interface SetCurrentBookDraftResponse extends BookDraftVersionsResponse {
  /** The draft the operator clicked. */
  sourceDraftId?: string;
  /** The copy made current, or null when the target was already editable. */
  copiedDraftId?: string | null;
  /** True when an existing untouched copy was reused instead of creating one. */
  copyReused?: boolean;
}

interface BbaEnvelope<T> {
  status?: boolean;
  message?: string;
  data?: T | null;
}

// BBA reports failures as HTTP 200 `{ status: false, message }` with no `data`.
// Throw with BBA's message so callers show it instead of crashing on `data.versions`.
// An empty message lets the caller fall back to its own copy.
const unwrapBba = <T>(body: BbaEnvelope<T> | undefined): T => {
  if (!body || body.status === false || body.data == null) {
    throw new Error(body?.message || '');
  }
  return body.data;
};

const withOperator = (operatorEmail: string) => ({
  headers: { 'X-Admin-Identity': operatorEmail },
});

export const getBookDraftVersions = async (
  email: string,
  draftId: string,
  operatorEmail: string,
): Promise<BookDraftVersionsResponse> => {
  const params =
    `?key=${import.meta.env.VITE_BBA_APIKEY}` +
    `&email=${encodeURIComponent(email)}` +
    `&draftId=${encodeURIComponent(draftId)}`;
  const response = await instance.get(
    `abo/draft/versions${params}`,
    withOperator(operatorEmail),
  );
  return unwrapBba<BookDraftVersionsResponse>(response.data);
};

export const setCurrentBookDraft = async (
  draftId: string,
  operatorEmail: string,
): Promise<SetCurrentBookDraftResponse> => {
  const params = `?key=${import.meta.env.VITE_BBA_APIKEY}`;
  const response = await instance.patch(
    `abo/draft/set-current${params}`,
    { draftId },
    withOperator(operatorEmail),
  );
  return unwrapBba<SetCurrentBookDraftResponse>(response.data);
};
